import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom"; // ✅ FIX: portal for dropdown
import { toast } from "react-hot-toast";
import { CheckCircle, ChevronDown, Clock, Zap, AlertCircle, Download } from "lucide-react";
import confetti from 'canvas-confetti';
import { getHubDetails, getAvailableSlots, selectSlot } from "../../api/userApi.js";
import { useToast } from "../../contexts/ToastContext";
import { toPng } from 'html-to-image';

const CompetitionHub = () => {
  const { competitionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showToast = useToast();
  const cardRef = useRef(null);
  const lastNoticeRef = useRef(null);
  const slotButtonRef = useRef(null); // ✅ FIX: ref to anchor dropdown position

  // --- STATE ---
  const [data, setData] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [highlightSlot, setHighlightSlot] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 }); // ✅ FIX: track position

  // --- DATE CALCULATIONS ---
  const now = new Date();
  const rawEndDate = data?.end_date ? new Date(data.end_date) : new Date();
  const competitionDate = new Date(rawEndDate);
  const battleDate = new Date(rawEndDate);
  if (data) battleDate.setDate(battleDate.getDate() + 1);

  const isDeadlinePassed = data?.end_date
    ? new Date() > new Date(data.end_date)
    : false;

  // --- TOAST ON QUIZ REDIRECT ---
  useEffect(() => {
    if (location.state?.quizResult) {
      const { quizResult, finalScore } = location.state;
      if (quizResult === "PASSED") {
        showToast(`Congratulations! You qualify for Stage 2! Score: ${finalScore}%`, "success");
      } else {
        showToast(`Stage 1 Failed. Score: ${finalScore}%. Download your participation certificate.`, "error");
      }
      window.history.replaceState({}, document.title);
    }
  }, [location, showToast]);

  // --- SLOT HIGHLIGHT ---
  useEffect(() => {
    if (data && !data.selected_slot_id) {
      const timer = setTimeout(() => setHighlightSlot(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [data]);

  // --- POLLING: 15s — admin can unlock at any time ---
  useEffect(() => {
    if (!competitionId) return;
    fetchHubData();
    const interval = setInterval(() => fetchHubData(), 15000);
    return () => clearInterval(interval);
  }, [competitionId]);

  const fetchHubData = async () => {
    try {
      const hub = await getHubDetails(competitionId);
      if (hub.notice_text && hub.notice_text !== lastNoticeRef.current) {
        showToast("New update on the Notice Board!", "info");
      }
      lastNoticeRef.current = hub.notice_text;
      setData(hub);
      if (!hub.selected_slot_id) {
        const slots = await getAvailableSlots(competitionId);
        setAvailableSlots(slots);
      }
    } catch (err) {
      console.error("Sync failed");
    }
  };

  // ✅ FIX: Calculate dropdown position from button ref before showing
  const handleToggleDropdown = () => {
    if (!showDropdown && slotButtonRef.current) {
      const rect = slotButtonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setShowDropdown(prev => !prev);
    setHighlightSlot(false);
  };

  // --- SLOT SELECTION ---
  const handleSlotSelection = async (slotId, label) => {
    try {
      setLoading(true);
      setShowDropdown(false);
      const response = await selectSlot(competitionId, slotId);
      if (response.success) {
       showToast(`Battle Time Locked: ${label}`, "success");
        await fetchHubData();
      }
    } catch (err) {
      toast.error("Selection failed.");
    } finally {
      setLoading(false);
    }
  };

  // --- VICTORY CARD DOWNLOAD ---
  const downloadVictoryCard = async () => {
    if (cardRef.current === null) return;
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 3 });
      const link = document.createElement('a');
      link.download = `Arena-Champion-${data.competition_title}.png`;
      link.href = dataUrl;
      link.click();
      showToast("Champion Card Saved!", "success");
    } catch (err) {
      showToast("Download failed, try a screenshot!", "error");
    }
  };

  // --- STAGE NAVIGATION ---
  const handleStage1Click = () => {
    if (data.stage_1_status === 'completed') {
      showToast("Stage 1 already cleared!", "success");
      return;
    }
    if (data.stage_1_status === 'failed') {
      showToast("Stage 1 failed. You are no longer eligible.", "error");
      return;
    }
    if (data.stage_1_status === 'locked') {
      showToast("Stage 1 is locked. Wait for the admin to open it.", "info");
      return;
    }
    // status === 'unlocked'
    navigate(`/arena/${competitionId}`);
  };

  // Stage 2 is purely DB driven — admin unlocks Stage 1, passing auto-unlocks Stage 2
  const handleStage2Click = () => {

  // BLOCK 1: User is eliminated at ANY stage
  // Covers: eliminated after stage 1 fail, after project rejection, after interview rejection
  if (data.status === 'eliminated') {
    showToast("You have been eliminated from this competition. Check your Achievements for your certificate.", "error");
    return;
  }

  // BLOCK 2: Stage 1 was failed (disqualified during quiz)
  if (data.stage_1_status === 'failed') {
    showToast("Stage 1 was failed. You are not eligible for Stage 2.", "error");
    return;
  }

  //  BLOCK 3: Stage 1 not yet completed (still locked or unlocked but not done)
  if (data.stage_1_status !== 'completed') {
    showToast("Complete Stage 1 first to unlock Stage 2.", "info");
    return;
  }

  //  BLOCK 4: Stage 2 submission was failed/rejected
  // This covers: admin rejected project, deadline expired, disqualified in stage 2
  if (data.stage_2_status === 'failed') {
    showToast("Stage 2 was closed. Check the Notice Board for details.", "error");
    return;
  }

  // BLOCK 5: Already submitted successfully — don't allow resubmit
  if (data.stage_2_status === 'completed') {
    showToast("Project already submitted! Await evaluation from our team.", "success");
    return;
  }

  //  BLOCK 6: Stage 2 still locked (passed stage 1 but stage 2 not started)
  if (data.stage_2_status === 'locked') {
    showToast("Stage 2 will unlock once Stage 1 is cleared.", "info");
    return;
  }

  //  PASSES ALL CHECKS: stage_2_status === 'unlocked' — go ahead
  navigate(`/mission-stage-two/${competitionId}`);
};

  // Victory — no time gate, purely status driven
  const handleVictoryClick = () => {
    if (data.status === 'winner') {
      setShowVictoryModal(true);
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) return clearInterval(interval);
        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: Math.random(), y: Math.random() - 0.2 } });
      }, 250);
    } else {
      showToast("Victory Hall is reserved for Champions only.", "error");
    }
  };

  if (!data)
    return (
      <div className="min-h-screen bg-black flex items-center justify-center font-mono text-blue-500 uppercase tracking-widest animate-pulse">
        Initializing_Arena_Core...
      </div>
    );

  return (
    <div className="min-h-screen bg-black text-gray-300 p-4 md:p-8 font-sans selection:bg-blue-500/30 transition-all duration-500">
      

      {/* ── HEADER ─────────────────────────────────────────────────────────── */}
      {/* 
        ✅ FIX: Changed from grid to flex so the slot dropdown is NOT inside 
        a grid cell that clips overflow. Overflow:visible works on flex but 
        CSS grids clip absolutely-positioned children in many browsers.
        Using portal anyway for belt-and-suspenders safety.
      */}
      <div className="max-w-[1400px] mx-auto flex justify-between items-start gap-4 mb-8 border-b border-gray-900 pb-6">
        
        {/* LEFT: Competition info */}
        <div>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] mb-1">Competition Name</p>
          <h1 className="text-base font-bold text-white uppercase tracking-tight">{data.competition_title}</h1>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] mt-4 mb-1">Battle Date</p>
          <p className="text-sm font-mono text-blue-500">{battleDate.toDateString()}</p>
        </div>

        {/* RIGHT: ID + Slot selector */}
        <div className="text-right shrink-0">
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] mb-1">Competition ID</p>
          <p className="text-sm font-mono text-white">#{competitionId.slice(0, 8).toUpperCase()}...</p>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] mt-4 mb-1">Active Slot</p>

          {/* CASE 1: Slot already selected */}
          {data.selected_slot_id ? (
            <div className="flex items-center gap-2 justify-end text-green-500 font-bold font-mono uppercase">
              <span className="text-sm">{data.slot_label || "LOCKED"}</span>
              <CheckCircle size={14} />
            </div>

          /* CASE 2: Deadline passed, no slot */
          ) : isDeadlinePassed ? (
            <div className="flex items-center gap-2 justify-end text-white font-bold font-mono uppercase">
              <span className="text-sm">🔒 11:00 PM</span>
            </div>

          /* CASE 3: Before deadline — slot selector */
          ) : (
            <div className="relative inline-block">

              {/* Highlight tooltip */}
              {highlightSlot && (
                <div className="absolute -top-12 right-0 bg-blue-600 text-white text-[9px] px-3 py-1.5 rounded-md animate-bounce font-black whitespace-nowrap shadow-[0_0_15px_rgba(37,99,235,0.5)] z-[60]">
                  SELECT YOUR BATTLE TIME! ↓
                  <div className="absolute -bottom-1 right-4 w-2 h-2 bg-blue-600 rotate-45" />
                </div>
              )}

              {/* ✅ FIX: Button with ref so we can measure its position */}
              <button
                ref={slotButtonRef}
                onClick={handleToggleDropdown}
                className={`flex items-center gap-2 text-white text-[11px] font-bold px-3 py-1.5 rounded transition-all uppercase tracking-tighter ${
                  highlightSlot
                    ? "bg-blue-500 ring-4 ring-blue-500/30 scale-105 shadow-[0_0_25px_rgba(59,130,246,0.4)]"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                <Clock size={12} />
                {showDropdown ? "CLOSE LIST" : "SELECT SLOT"}
                <ChevronDown
                  size={12}
                  className={`transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {/* ✅ FIX: Render dropdown via portal so it escapes ALL parent overflow/transform stacking contexts */}
              {showDropdown && createPortal(
                <>
                  {/* Invisible backdrop — click outside to close */}
                  <div
                    className="fixed inset-0 z-[9998]"
                    onClick={() => setShowDropdown(false)}
                  />

                  {/* Dropdown panel — positioned using measured coords */}
                  <div
                    className="fixed z-[9999] w-56 bg-[#0f0f0f] border border-gray-700 rounded-xl shadow-2xl overflow-hidden"
                    style={{ top: dropdownPos.top, right: dropdownPos.right }}
                  >
                    {/* Header label */}
                    <div className="px-4 py-2.5 border-b border-gray-800 bg-black/40">
                      <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                        Choose Your Battle Slot
                      </p>
                    </div>

                    {/* Slot options */}
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot) => (
                        <button
                          key={slot.id}
                          onClick={() => handleSlotSelection(slot.id, slot.slot_label)}
                          className="w-full text-left px-4 py-3 text-xs text-gray-300 hover:bg-blue-600 hover:text-white transition-colors border-b border-gray-900 last:border-0 flex items-center gap-2"
                        >
                          <Clock size={11} className="text-blue-400 shrink-0" />
                          <span className="font-bold">{slot.slot_label}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-4 text-[10px] text-gray-500 italic text-center">
                        No slots available.
                      </div>
                    )}
                  </div>
                </>,
                document.body // ✅ Renders outside ALL parent containers
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN CONTENT ───────────────────────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">

        {/* LEFT: Journey stages */}
        <div className="lg:col-span-8">
          <div className="bg-red-900/10 border border-red-500/20 p-4 rounded-xl mb-10 flex items-center gap-3">
            <AlertCircle size={16} className="text-red-500" />
            <p className="text-[11px] text-red-400 font-medium">
              Note: Select your battle slot before <span>{competitionDate.toDateString()}</span>. If not selected, the system defaults to 11:00 PM.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-[39px] top-10 bottom-10 w-[2px] bg-gradient-to-b from-blue-600 via-gray-800 to-transparent" />

            {/* STAGE 1 */}
            <div className="flex gap-10 mb-12 group">
              <StageCircle num="1" status={data.stage_1_status} onClick={handleStage1Click} />
              <div className={`flex-1 bg-[#080808] border p-6 rounded-2xl transition-all ${data.stage_1_status === 'unlocked' ? 'border-blue-500 animate-pulse' : 'border-gray-900'}`}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-white font-bold text-sm uppercase">Stage 1: Domain Knowledge Test</h3>
                  <StatusTag status={data.stage_1_status} />
                </div>
                {data.stage_1_status === 'failed' ? (
                  <p className="text-[11px] text-red-500 font-bold uppercase tracking-tighter">
                    Stage 1 Failed. You are no longer eligible. Download your Participation Certificate from Achievements.
                  </p>
                ) : (
                  <ul className="text-[11px] text-gray-500 space-y-2 list-disc ml-4">
                    <li>If a slot is not selected, the default slot will be allotted - {data.slot_label || "11:00 PM"}</li>
                    <li>You need a minimum of 70% score to qualify.</li>
                    <li>Monitoring is active; browser switching results in disqualification.</li>
                  </ul>
                )}
              </div>
            </div>

            {/* STAGE 2 */}
            <div className="flex gap-10 mb-12 group">
              <StageCircle num="2" status={data.stage_2_status} onClick={handleStage2Click} />
              <div className="flex-1 bg-[#080808] border border-gray-900 p-6 rounded-2xl group-hover:border-gray-700 transition-all">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-white font-bold text-sm uppercase">Stage 2: Project Building Phase</h3>
                  <StatusTag status={data.stage_2_status} />
                </div>
                <ul className="text-[11px] text-gray-500 space-y-2 list-disc ml-4">
                  <li>Build a solution based on the problem statement.</li>
                  <li>24-hour window for final submission.</li>
                  <li>Develop the project and deploy it to GitHub and/or other hosting platforms.</li>
                  <li>Submit the live project URL after deployment.</li>
                  <li>After submission, our team will review the project and inform you about the next steps.</li>
                </ul>
              </div>
            </div>

            {/* STAGE 3: LIVE INTERVIEW */}
            <div className="flex gap-10 mb-12 group">
              <StageCircle
                num="3"
                status={data.status === 'interview' ? 'unlocked' : (data.status === 'winner' ? 'completed' : 'locked')}
                onClick={() => {
                  if (data.status === 'interview') {
                    showToast("Check notice board for interview details.", "info");
                  } else if (data.status === 'winner') {
                    showToast("Interview completed. You are a Champion!", "success");
                  } else {
                    showToast("Stage 3 will be scheduled after project review.", "info");
                  }
                }}
              />
              <div className={`flex-1 bg-[#080808] border p-6 rounded-2xl transition-all ${data.status === 'interview' ? 'border-blue-500 shadow-lg' : 'border-gray-900'}`}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-white font-bold text-sm uppercase">Stage 3: Live Project Assessment</h3>
                  <StatusTag status={data.status === 'interview' ? 'unlocked' : (data.status === 'winner' ? 'completed' : 'locked')} />
                </div>
                <ul className="text-[11px] text-gray-500 space-y-2 list-disc ml-4">
                  <li>Our team will contact you for a live meeting regarding your project.</li>
                  <li>During the meeting, we will ask questions about your project to understand it better.</li>
                  <li>After the interview, we will decide the result based on your performance.</li>
                  <li>Make sure to prepare well before the live meeting.</li>
                  <li>Congratulations if you've reached this stage — it shows you are capable of a lot!</li>
                  <li>Keep pushing forward and stay confident!</li>
                </ul>
              </div>
            </div>

            {/* FINAL: VICTORY HALL */}
            <div className="flex gap-10 group">
              <StageCircle
                num="🏆"
                status={data.status === 'winner' ? 'unlocked' : 'locked'}
                onClick={handleVictoryClick}
              />
              <div className={`flex-1 bg-[#080808] border p-6 rounded-2xl transition-all ${data.status === 'winner' ? 'border-yellow-500/50 shadow-xl' : 'border-gray-900'}`}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-white font-bold text-sm uppercase">Final: Victory Hall</h3>
                  <StatusTag status={data.status === 'winner' ? 'unlocked' : 'locked'} />
                </div>
                <p className="text-[11px] text-gray-500">
                  {data.status === 'winner'
                    ? "You are an Arena Champion! Click the trophy to celebrate."
                    : "The Hall of Champions awaits the final evaluation."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Live Notice Board */}
        <div className="lg:col-span-4">
          <div className="bg-[#050505] border border-gray-800 rounded-2xl h-full flex flex-col min-h-[500px]">
            <div className="p-5 border-b border-gray-900 flex justify-between items-center">
              <h2 className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-500">Live Notice Board</h2>
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            </div>
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              {data.notice_text ? (
                <div className="bg-white/5 p-4 rounded-lg border border-blue-500/20 transition-all">
                  <p className="text-[9px] font-black mb-1 tracking-[0.2em] text-blue-500">[Notice]</p>
                  <p className="text-xs text-gray-400 leading-relaxed">{data.notice_text}</p>
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center opacity-20">
                  <Zap size={40} className="mb-2" />
                  <p className="text-[10px] font-mono tracking-widest uppercase">Waiting_For_Updates...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto mt-20 border-t border-white/5 pt-10 mb-20 px-6">
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2 bg-blue-500 rounded-full animate-pulse" />
            <h2 className="text-[10px] font-black uppercase tracking-[0.5em] text-blue-500 italic">How TO PARTICIPATE</h2>
          </div>
          <span className="text-[9px] text-gray-600 font-mono hidden md:block uppercase">v2.0_Live_Monitoring_Enabled</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6">
          <ParticipationStep num="01" title="Slot Select" desc="Choose your timeline from the top right." />
          <ParticipationStep num="02" title="Initiate" desc="Click step circles to enter the arena." />
          <ParticipationStep num="03" title="Execute" desc="Stay focused. Camera active for anti-cheat." isWarning />
          <ParticipationStep num="04" title="Observe" desc="Monitor results on the Notice Board." />
          <ParticipationStep num="05" title="Victory" desc="Win & Flex. Share your trophy." />
          <ParticipationStep num="06" title="Legacy" desc="Go to Achievements to claim your certificates." />
        </div>
      </div>

      {/* ── VICTORY MODAL ──────────────────────────────────────────────────── */}
      {showVictoryModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-2xl">
          <div className="w-full max-w-[500px] animate-in zoom-in duration-300">
            <div ref={cardRef} className="relative w-full bg-[#080808] border border-white/10 p-8 overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#111_0%,_transparent_50%)]" />
              <div className="relative flex justify-between items-center mb-10">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-black border border-white/20 flex items-center justify-center text-[10px] font-black text-white">GZ</div>
                  <span className="text-xs font-black tracking-tighter text-white italic">GenZ<span className="text-blue-500">Evaluation</span></span>
                </div>
                <div className="flex items-center gap-2 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                  <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Verified</span>
                  <CheckCircle size={14} className="text-emerald-500" />
                </div>
              </div>
              <div className="relative mb-8">
                <h2 className="text-sm font-bold text-gray-400 flex items-center gap-2 uppercase tracking-widest mb-2">Congratulations 🎊</h2>
                <div className="flex items-baseline gap-2">
                  <h1 className="text-6xl font-black text-white italic tracking-tighter leading-none">TOP</h1>
                  <h1 className="text-6xl font-black text-blue-500 italic tracking-tighter leading-none">5%</h1>
                </div>
                <p className="mt-4 text-lg font-black text-white uppercase italic tracking-tight opacity-90">{data.competition_title}</p>
              </div>
              <div className="relative pt-6 border-t border-white/5 flex justify-between items-center">
                <div>
                  <p className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mb-0.5">Issue Date</p>
                  <p className="text-[10px] font-black text-white italic uppercase tracking-wider">
                    {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[8px] font-bold text-gray-500 uppercase tracking-widest mb-0.5">Credential ID</p>
                  <p className="text-[10px] font-mono text-white/60">#{competitionId.slice(0, 10).toUpperCase()}</p>
                </div>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={downloadVictoryCard} className="flex items-center justify-center gap-2 bg-white text-black py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all active:scale-95">
                <Download size={16} /> Save Card
              </button>
              <button onClick={() => setShowVictoryModal(false)} className="flex items-center justify-center bg-white/5 border border-white/10 text-white py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest hover:bg-red-500 transition-all">
                Close
              </button>
            </div>
            <p className="mt-4 text-center text-[9px] text-gray-600 font-bold uppercase tracking-[0.3em]">You Did It</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Helper Components ──────────────────────────────────────────────────────────

const StageCircle = ({ num, status, onClick }) => {
  const isDone     = status === "completed";
  const isFailed   = status === "failed";
  const isUnlocked = status === "unlocked";

  return (
    <button
      onClick={onClick}
      className={`w-20 h-20 rounded-full border-2 flex flex-col items-center justify-center transition-all z-10 shrink-0
        ${isDone     ? "bg-green-600 border-green-400 text-white" :
          isFailed   ? "bg-red-900 border-red-500 text-white" :
          isUnlocked ? "bg-blue-600 border-blue-400 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)]" :
                       "bg-white border-blue-600 text-black"}
      `}
    >
      <span className="text-[10px] font-black uppercase opacity-60">Step</span>
      {isDone   ? <CheckCircle size={24} /> :
       isFailed ? <AlertCircle size={24} /> :
                  <span className="text-2xl font-black">{num}</span>}
    </button>
  );
};

const StatusTag = ({ status }) => (
  <span className={`text-[9px] px-2 py-0.5 rounded border font-bold uppercase tracking-widest
    ${status === "completed" ? "bg-green-500/10 text-green-500 border-green-500/30" :
      status === "failed"    ? "bg-red-500/10 text-red-500 border-red-500/30" :
                               "bg-blue-500/10 text-blue-500 border-blue-500/30"}`}>
    {status || "locked"}
  </span>
);

const ParticipationStep = ({ num, title, desc }) => (
  <div className="group">
    <div className="flex items-center gap-4 mb-3">
      <span className="text-2xl font-black text-white/10 group-hover:text-blue-500/50 transition-colors">{num}</span>
      <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">{title}</h4>
    </div>
    <p className="text-[11px] text-gray-500 leading-relaxed">{desc}</p>
  </div>
);

export default CompetitionHub;